import { LitElement, html, property } from 'lit-element'
import { unsafeSVG } from 'lit-html/directives/unsafe-svg'
import { until } from 'lit-html/directives/until.js'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import './graphic.scss'

/**
 * List of all valid types that this element supports.
 * @type {Array}
 */
const validTypes = [
  'icon',
  'illustration',
  'text',
  'legacy-glyph',
  'legacy-svg'
]

/**
 * Mapping of deprecated types and their equivalent aliases.
 * @type {Object}
 */
const deprecatedTypes = {
  glyph: 'icon',
  svg: 'illustration',
  graphic: 'illustration'
}

/**
 * Graphic element lets consumers easily render SVG icons, SVG illustrations,
 * text icons and legacy icon font glyphs. Consumers must provide both the
 * `type` and `name` of the icon to render. Omitting the `type` field implies
 * the `illustration` type by default. Text-based icons uses the first two
 * characters in `name` as the icon.
 *
 * ```
 * // Render illustrations, icons and text icons.
 * <pe-graphic name='my-illustration'></pe-graphic>
 * <pe-graphic type='icon' name='folder'></pe-graphic>
 * <pe-graphic type='text' name='da'></pe-graphic>
 *
 * // To render legacy illustrations and icon glyphs.
 * <pe-graphic type='legacy-svg' name='ok-green'></pe-graphic>
 * <pe-graphic type='legacy-glyph' name='anonymous'></pe-graphic>
 *
 * // Render custom SVGs that are stored within your own package b passing them
 * // in via default slot and setting `type=custom`.
 * <pe-graphic type='custom'>
 *   <svg id=arrow xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
 *     <path d="M22 27.109 10.804 16 22 4.891 20.598 3.5 8 16l12.598 12.5z"/>
 *   </svg>
 * </pe-graphic>
 *
 * // Apply custom colors to icons using CSS properties.
 * <pe-graphic style='--pe-graphic-color: blue' type='icon' name='folder'></pe-graphic>
 *
 * // Scale the graphic up or down using CSS properties.
 * <pe-graphic style='--pe-graphic-size: 2rem' name='blog'></pe-graphic>
 * ```
 *
 * @element pe-graphic
 *
 * @prop {string} [type=illustration] - Selects the type of graphic to render.
 *  Valid types are `icon`, `illustration`, `text`, `custom`, `legacy-svg`,
 *  `legacy-glyph`. DEPRECATED types are `graphic`, `svg`, `glyph` and act as
 *  aliases to `illustration` and `icon` respectively. Default type when field is
 *  omitted is `illustration`.
 * @prop {string} name - For `icon` and `illustration` types, this is the SVG
 *  file name. For text-based icons, the first two characters are used.
 *
 * @cssprop [--pe-graphic-color=pe-neutral-900] - Apply primary color to all
 *  icon types only. Graphic types do not support custom color settings.
 * @cssprop --pe-graphic-secondary-color - Apply a secondary color to all icons
 *  that support multiple colors to create duotone icons. Graphic types do not
 *  support custom color settings.
 * @cssprop --pe-graphic-size - Change the size of the graphic.
 *
 * @slot - When `type=custom`, pass in your own custom raw SVG which gets
 *  rendered by the element and lets you scale the SVG in a consistent way.
 */
export default class Graphic extends LitElement {
  static get tagName () { return 'pe-graphic' }

  @property({ type: String })
  get type () {
    return this._type || 'illustration'
  }

  set type (value) {
    const oldValue = this._type
    if (value === oldValue) return

    this._type = value
    this.requestUpdate('type', oldValue)
  }

  @property({ type: String })
  name

  createRenderRoot () { return this }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  render () {
    if (this.type in deprecatedTypes) {
      console.warn(`${this.tagName}[name=${this.name}] has [type=${this.type}] and should be replaced with [type=${deprecatedTypes[this.type]}]`)
    }

    return until(
      renderText.call(this) ||
      renderLegacyGlyph.call(this) ||
      renderSvg.call(this),
      html`<div class=${bem('placeholder')}></div>`
    )
  }
}

/**
 * Returns a promise that resolves with the SVG template result or an empty
 * string if the type is not handled here. Raw SVG is fetched from different
 * paths depending on the type. Promise is rejected when SVG file is not found.
 * @return {Promise} Resolves to the SVG template result or an empty string if
 *  type is not supported. Rejects with an error if SVG import fails.
 */
async function renderSvg () {
  let rawSvg = ''

  switch (this.type) {
    case 'custom': {
      // Renders the SVG passed in via default slot. To support this with our
      // custom slots API, we have to iterate through each node and convert the
      // text node containing <svg> markup into legitimate SVG template results.
      let hasSvg = false
      const slots = this.slots().map(node => {
        const isSvgNode = /^svg$/i.test(node.tagName)
        const isUnsafeSvgNode = /^<svg.*>.*<\/svg>$/.test(node.data)
        hasSvg = hasSvg || isSvgNode || isUnsafeSvgNode
        return isUnsafeSvgNode ? unsafeSVG(node.data) : node
      })

      // Error check to ensure this type only renders SVGs passed into the slot.
      if (!hasSvg) {
        throw new Error(`${this.tagName}[type=${this.type}] must have SVG content in its default slot but is missing`)
      }
      return slots
    }

    case 'svg':
    case 'graphic':
    case 'illustration':
      rawSvg = (await import(
        /* webpackChunkName: 'illustration-[request]' */
        `./illustrations/${this.name}.svg`
      )).default
      break

    case 'glyph':
    case 'icon':
      rawSvg = (await import(
        /* webpackChunkName: 'icon-[request]' */
        `./icons/${this.name}.svg`
      )).default
      break

    case 'legacy-svg':
      rawSvg = (await import(
        /* webpackChunkName: 'legacy-[request]' */
        `@polleverywhere/assets/icons/${this.name}.svg`
      )).default
      break

    default:
      throw new Error(`${this.tagName}[name=${this.name}] has invalid [type=${this.type}] and must be one of [${validTypes.join(',')}]`)
  }

  return rawSvg ? unsafeSVG(rawSvg) : ''
}

/**
 * Renders a text-based icon with the first two letters of "name".
 */
function renderText () {
  if (this.type !== 'text') return ''

  // Uppercase first two letters of icon text to render into an icon.
  const initials = this.name.substring(0, 2).toUpperCase()
  return html`
  <div class=${bem('text')} name='${initials}' aria-hidden='true'>
    <span class=${bem('text-label')}>${initials}</span>
  </div>`
}

/**
 * Renders a legacy glyph template.
 */
function renderLegacyGlyph () {
  if (this.type !== 'legacy-glyph') return ''

  return html`
  <span class=${bem('legacy-glyph')}
      data-glyph='${this.name}'
      aria-hidden='true'></span>`
}

const bem = bemBlock(Graphic.tagName)
register(module, Graphic.tagName, Graphic)
